import { useState, useEffect } from 'react';
import { MapPin, X, Save, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  useLocationFilter,
  useSaveLocationFilter,
  useClearLocationFilter
} from '@/hooks/use-admin';

const SWEDISH_COUNTIES = [
  'Blekinge',
  'Dalarna',
  'Gotland',
  'Gävleborg',
  'Halland',
  'Jämtland',
  'Jönköping',
  'Kalmar',
  'Kronoberg',
  'Norrbotten',
  'Skåne',
  'Stockholm',
  'Södermanland',
  'Uppsala',
  'Värmland',
  'Västerbotten',
  'Västernorrland',
  'Västmanland',
  'Västra Götaland',
  'Örebro',
  'Östergötland'
] as const;

function parseRegions(raw: string): string[] {
  return raw
    .split(',')
    .map((r) => r.trim())
    .filter(Boolean);
}

export function LocationFilterCard() {
  const { data: filterData, isLoading } = useLocationFilter();
  const saveFilter = useSaveLocationFilter();
  const clearFilter = useClearLocationFilter();

  const [regionsInput, setRegionsInput] = useState('');
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);

  // Sync local state when remote data loads
  useEffect(() => {
    if (filterData?.data) {
      setRegionsInput(filterData.data.regions.join(', '));
      setSelectedCounties(filterData.data.counties);
    }
  }, [filterData]);

  const activeRegions = filterData?.data?.regions ?? [];
  const activeCounties = filterData?.data?.counties ?? [];
  const hasActiveFilter = activeRegions.length > 0 || activeCounties.length > 0;

  function toggleCounty(county: string) {
    setSelectedCounties((prev) =>
      prev.includes(county) ? prev.filter((c) => c !== county) : [...prev, county]
    );
  }

  function handleSave() {
    saveFilter.mutate({
      regions: parseRegions(regionsInput),
      counties: selectedCounties
    });
  }

  function handleClear() {
    clearFilter.mutate(undefined, {
      onSuccess: () => {
        setRegionsInput('');
        setSelectedCounties([]);
      }
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4" />
          Auto-Match Location Filter
          {hasActiveFilter && (
            <Badge
              variant="outline"
              className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
            >
              Active — {activeRegions.length + activeCounties.length} location
              {activeRegions.length + activeCounties.length === 1 ? '' : 's'}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading filter config...</p>
        ) : (
          <>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Specific cities / regions (comma-separated)
              </label>
              <input
                type="text"
                value={regionsInput}
                onChange={(e) => setRegionsInput(e.target.value)}
                placeholder="e.g. Malmö, Lund, Helsingborg"
                className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">Counties (lan)</label>
              <div className="flex flex-wrap gap-1.5">
                {SWEDISH_COUNTIES.map((county) => {
                  const active = selectedCounties.includes(county);
                  return (
                    <button
                      key={county}
                      type="button"
                      onClick={() => toggleCounty(county)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                        active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-foreground hover:bg-muted'
                      }`}
                    >
                      {county}
                    </button>
                  );
                })}
              </div>
            </div>

            {hasActiveFilter && (
              <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                <strong>Currently active:</strong>{' '}
                {[...activeRegions, ...activeCounties.map((c) => `${c} (county)`)].join(', ')}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saveFilter.isPending}
                className="flex-1"
              >
                <Save className="mr-1.5 h-3.5 w-3.5" />
                {saveFilter.isPending ? 'Saving...' : 'Save Filter'}
              </Button>
              {hasActiveFilter && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClear}
                  disabled={clearFilter.isPending}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  {clearFilter.isPending ? 'Clearing...' : 'Clear'}
                </Button>
              )}
            </div>

            {!hasActiveFilter && (
              <p className="text-xs text-muted-foreground">
                No filter active — bulk auto-matching considers all assignment locations.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
